import React, { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import { UserPlus, Check } from "lucide-react";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const IndividualMembershipPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    age_group: "mini",
    phone: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/members/individual`, {
        ...formData,
        fee: 15.0
      });
      toast.success("Individual membership created!");
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create membership");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6" data-testid="membership-success">
        <Card className="border-status-success/30 bg-status-success/5">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-status-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-status-success" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Membership Created!
            </h2>
            <p className="text-text-secondary mb-6">
              {formData.first_name} {formData.last_name} is now a member.
            </p>
            <Button
              onClick={() => {
                setSuccess(false);
                setFormData({ first_name: "", last_name: "", age_group: "mini", phone: "" });
              }}
              className="bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Create Another
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="individual-membership-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
          <UserPlus className="h-6 w-6 text-primary" />
          Individual Membership
        </h1>
        <p className="text-text-secondary mt-1">$15 per person</p>
      </div>

      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Member Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="individual-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="ind-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="ind-lastname"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Age Group *</Label>
              <Select
                value={formData.age_group}
                onValueChange={(value) => setFormData({ ...formData, age_group: value })}
              >
                <SelectTrigger data-testid="ind-agegroup">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGE_GROUPS.map((ag) => (
                    <SelectItem key={ag} value={ag} className="capitalize">
                      {ag.charAt(0).toUpperCase() + ag.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Optional"
                data-testid="ind-phone"
              />
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-4">
                <span className="font-heading font-bold text-text-primary">Total</span>
                <span className="font-heading font-bold text-2xl text-accent">$15.00</span>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-accent text-white hover:bg-accent/90 font-bold h-12"
                data-testid="ind-submit"
              >
                {submitting ? "Processing..." : "Create Membership ($15)"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Card className="border-primary/20 bg-secondary/30">
        <CardContent className="p-4">
          <h3 className="font-heading font-bold text-text-primary mb-2">Member Benefits</h3>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• Earn points towards year-end standings</li>
            <li>• Eligible for year-end awards</li>
            <li>• Support your local play day organization</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default IndividualMembershipPage;
